import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material";
import {
  Box,
  TextField,
  Grid,
  Divider,
  Chip,
  Card,
  CardContent,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormHelperText,
  Breadcrumbs,
  Link,
  Typography,
  Modal,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Restricted from "components/Restricted";

import HomeIcon from "@mui/icons-material/Home";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { School } from "@mui/icons-material";

// Función para capitalizar la primera letra de cada palabra
const capitalizeWords = (str) => {
  if (!str) return "";
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Función para limpiar espacios en blanco de todos los strings del formulario
const trimFormData = (data) => {
  const trimmedData = {};
  for (const key in data) {
    const value = data[key];
    if (typeof value === "string") {
      trimmedData[key] = value.trim();
    } else {
      trimmedData[key] = value; // Mantener otros tipos de datos como están
    }
  }
  return trimmedData;
};

function EditarCapForm() {
  const [items, setItems] = React.useState([]);

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem("token"));
    if (items) {
      setItems(items);
    }
  }, []);

  const rol = items.rol;

  const nav = useNavigate();

  //Theme
  const theme = useTheme();

  // Estado para manejar los valores del formulario
  const [formData, setFormData] = useState({
    // Sección Actividad
    tematica: "",
    estado: "",
    municipio: "",
    parroquia: "",
    comunidadActividad: "",
    // Sección Beneficiario
    primerNombre: "",
    primerApellido: "",
    cedulaIdentidad: "",
    genero: "",
    lgbtqi: "",
    fechaNacimiento: null,
    comunidadBeneficiario: "",
    grupoEtnico: "",
    discapacidad: "",
    condicionMovilidad: "",
  });

  // Estado para manejar los errores de validación
  const [errors, setErrors] = useState({});

  // Función para validar el formulario
  const validate = () => {
    const newErrors = {};
    const requiredFields = [
      "tematica",
      "primerNombre",
      "primerApellido",
      "genero",
      "lgbtqi",
      "fechaNacimiento",
      "comunidadBeneficiario",
      "grupoEtnico",
      "discapacidad",
      "condicionMovilidad",
    ];

    requiredFields.forEach((field) => {
      if (
        !formData[field] ||
        (typeof formData[field] === "string" && !formData[field].trim())
      ) {
        newErrors[field] = "Campo obligatorio";
      }
    });

    return newErrors;
  };

  // Manejador de cambios para campos de texto y selects
  const handleChange = (event) => {
    const { name, value } = event.target;

    let processedValue = value;
    const fieldsToCapitalize = [
      "tematica",
      "primerNombre",
      "primerApellido",
      "comunidadBeneficiario",
    ];

    if (fieldsToCapitalize.includes(name)) {
      processedValue = capitalizeWords(value);
    }

    // Actualiza el valor del campo
    setFormData((prevData) => ({
      ...prevData,
      [name]: processedValue,
    }));

    if (errors[name]) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Manejador específico para el DatePicker
  const handleDateChange = (newDate) => {
    // Actualiza la fecha
    setFormData((prevData) => ({
      ...prevData,
      fechaNacimiento: newDate,
    }));

    if (errors.fechaNacimiento) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.fechaNacimiento;
        return newErrors;
      });
    }
  };

  // Manejador para el envío del formulario
  const handleSubmit = (event) => {
    event.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const cleanedData = trimFormData(formData);
      console.log("Formulario Válido. Enviando datos limpios:", cleanedData);
      // Aquí iría la lógica para enviar los datos a un backend
    } else {
      console.log("Errores de validación encontrados.");
    }
  };

  //BreadCrumbs
  function handleClickBreadCrumbs(event) {
    event.preventDefault();
  }

  //Modal Handlers
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  //Modal Styles
  const style = {
    position: "inherit",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "25%",
    bgcolor: theme.palette.background.alt,
    border: "2px solid #000",
    borderRadius: 4,
    boxShadow: 1,
    p: 4,
  };

  //Routes
  const handleLinkCancelar = (event, message) => {
    if (message === "cancelar") {
      nav(-1);
    }
  };

  const handleLinkHome = (event, message) => {
    if (message === "home") {
      nav("/inicio");
    }
  };

  const handleLinkTranscripcion = (event, message) => {
    if (message === "transcripcion") {
      nav("/transcripcion");
    }
  };

  const handleLinkConvos = (event, message) => {
    if (message === "convos") {
      nav(-1);
    }
  };

  return (
    <Box m="1.5rem 2.5rem">
      {rol === "1" ||
      rol === "2" ||
      rol === "6" ||
      rol === "8" ||
      rol === "9" ||
      rol === "10" ||
      rol === "11" ? (
        <>
          <Box
            role="presentation"
            onClick={handleClickBreadCrumbs}
            sx={{ mb: 3 }}
          >
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                underline="hover"
                sx={{ display: "flex", alignItems: "center" }}
                color="inherit"
                href="/inicio"
                onClick={(event) => handleLinkHome(event, "home")}
              >
                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Inicio
              </Link>
              <Link
                underline="hover"
                sx={{ display: "flex", alignItems: "center" }}
                color="inherit"
                href="/transcripcion"
                onClick={(event) =>
                  handleLinkTranscripcion(event, "transcripcion")
                }
              >
                <DriveFileRenameOutlineIcon
                  sx={{ mr: 0.5 }}
                  fontSize="inherit"
                />
                Transcripción
              </Link>
              <Link
                underline="hover"
                sx={{ display: "flex", alignItems: "center" }}
                color="inherit"
                href="/listaTranscripcion"
                onClick={(event) => handleLinkConvos(event, "convos")}
              >
                <AssignmentIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Convocatoria
              </Link>
              <Typography
                sx={{ display: "flex", alignItems: "center" }}
                color="text.primary"
              >
                <School sx={{ mr: 0.5 }} fontSize="inherit" />
                Editar Beneficiario
              </Typography>
            </Breadcrumbs>
            <Typography variant="h3" sx={{ fontWeight: "bold", mt: 2 }}>
              Editar Beneficiario (Capacitaciones)
            </Typography>
          </Box>
           {/*/////////////////  FORMULARIO  /////////////////////*/}
          <Container>
            <Card
              sx={{
                borderRadius: 8,
                boxShadow: 5,
                bgcolor: theme.palette.background.alt,
              }}
            >
              <CardContent>
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  noValidate
                  autoComplete="off"
                  sx={{ p: 2 }}
                >
                  {/* --- SECCIÓN 1: INFORMACIÓN DE LA ACTIVIDAD --- */}
                  <Divider sx={{ mb: 3 }}>
                    <Chip
                      label="INFORMACIÓN DE LA ACTIVIDAD"
                      sx={{ textTransform: "uppercase" }}
                    />
                  </Divider>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="tematica"
                        label="Temática"
                        value={formData.tematica}
                        onChange={handleChange}
                        variant="outlined"
                        error={!!errors.tematica}
                        helperText={errors.tematica}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        disabled
                        name="estado"
                        label="Estado"
                        value={formData.estado}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        disabled
                        name="municipio"
                        label="Municipio"
                        value={formData.municipio}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        disabled
                        name="parroquia"
                        label="Parroquia"
                        value={formData.parroquia}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        disabled
                        name="comunidadActividad"
                        label="Comunidad"
                        value={formData.comunidadActividad}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>

                  {/* --- SECCIÓN 2: INFORMACIÓN DEL BENEFICIARIO --- */}
                  <Divider sx={{ mt: 5, mb: 3 }}>
                    <Chip
                      label="INFORMACIÓN DEL BENEFICIARIO"
                      sx={{ textTransform: "uppercase" }}
                    />
                  </Divider>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        name="primerNombre"
                        label="Primer Nombre"
                        value={formData.primerNombre}
                        onChange={handleChange}
                        variant="outlined"
                        error={!!errors.primerNombre}
                        helperText={errors.primerNombre}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        name="primerApellido"
                        label="Primer Apellido"
                        value={formData.primerApellido}
                        onChange={handleChange}
                        variant="outlined"
                        error={!!errors.primerApellido}
                        helperText={errors.primerApellido}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        name="cedulaIdentidad"
                        label="Cédula de Identidad"
                        type="number"
                        value={formData.cedulaIdentidad}
                        onChange={handleChange}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth error={!!errors.genero}>
                        <InputLabel id="genero-label">Género</InputLabel>
                        <Select
                          labelId="genero-label"
                          name="genero"
                          value={formData.genero}
                          label="Género"
                          onChange={handleChange}
                        >
                          <MenuItem value="M">M</MenuItem>
                          <MenuItem value="F">F</MenuItem>
                          <MenuItem value="Otro">Otro</MenuItem>
                        </Select>
                        {errors.genero && (
                          <FormHelperText>{errors.genero}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth error={!!errors.lgbtqi}>
                        <InputLabel id="lgbtqi-label">LGBTQI+</InputLabel>
                        <Select
                          labelId="lgbtqi-label"
                          name="lgbtqi"
                          value={formData.lgbtqi}
                          label="LGBTQI+"
                          onChange={handleChange}
                        >
                          <MenuItem value="Si">Si</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                        {errors.lgbtqi && (
                          <FormHelperText>{errors.lgbtqi}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Fecha de Nacimiento"
                          value={formData.fechaNacimiento}
                          onChange={handleDateChange}
                          inputFormat="DD/MM/YYYY" // <-- AQUÍ ESTÁ LA MODIFICACIÓN
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={!!errors.fechaNacimiento}
                              helperText={errors.fechaNacimiento}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        name="comunidadBeneficiario"
                        label="Comunidad del Beneficiario"
                        value={formData.comunidadBeneficiario}
                        onChange={handleChange}
                        variant="outlined"
                        error={!!errors.comunidadBeneficiario}
                        helperText={errors.comunidadBeneficiario}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth error={!!errors.grupoEtnico}>
                        <InputLabel id="grupo-etnico-label">
                          Grupo Étnico
                        </InputLabel>
                        <Select
                          labelId="grupo-etnico-label"
                          name="grupoEtnico"
                          value={formData.grupoEtnico}
                          label="Grupo Étnico"
                          onChange={handleChange}
                        >
                          <MenuItem value="Mestizo">Mestizo</MenuItem>
                          <MenuItem value="Wayuu">Wayuu</MenuItem>
                          <MenuItem value="Añú">Añú</MenuItem>
                          <MenuItem value="Japreira">Japreira</MenuItem>
                          <MenuItem value="Yanomami">Yanomami</MenuItem>
                          <MenuItem value="Bari">Bari</MenuItem>
                        </Select>
                        {errors.grupoEtnico && (
                          <FormHelperText>{errors.grupoEtnico}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth error={!!errors.discapacidad}>
                        <InputLabel id="discapacidad-label">
                          Discapacidad
                        </InputLabel>
                        <Select
                          labelId="discapacidad-label"
                          name="discapacidad"
                          value={formData.discapacidad}
                          label="Discapacidad"
                          onChange={handleChange}
                        >
                          <MenuItem value="Física">Física</MenuItem>
                          <MenuItem value="Intelectual">Intelectual</MenuItem>
                          <MenuItem value="Sensorial">Sensorial</MenuItem>
                          <MenuItem value="Múltiple">Múltiple</MenuItem>
                          <MenuItem value="Ninguna">Ninguna</MenuItem>
                        </Select>
                        {errors.discapacidad && (
                          <FormHelperText>{errors.discapacidad}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        error={!!errors.condicionMovilidad}
                      >
                        <InputLabel id="condicion-movilidad-label">
                          Condición de Movilidad
                        </InputLabel>
                        <Select
                          labelId="condicion-movilidad-label"
                          name="condicionMovilidad"
                          value={formData.condicionMovilidad}
                          label="Condición de Movilidad"
                          onChange={handleChange}
                        >
                          <MenuItem value="Local">Local</MenuItem>
                          <MenuItem value="Desplazado Interno">
                            Desplazado Interno
                          </MenuItem>
                          <MenuItem value="Refugiado">Refugiado</MenuItem>
                          <MenuItem value="Retornado">Retornado</MenuItem>
                          <MenuItem value="Pendular">Pendular</MenuItem>
                        </Select>
                        {errors.condicionMovilidad && (
                          <FormHelperText>
                            {errors.condicionMovilidad}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* --- SECCIÓN DE BOTONES --- */}
                  <Divider sx={{ my: 4 }} />
                  <Grid container justifyContent="center">
                    <Grid item xs={12} sm={5}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 2,
                        }}
                      >
                        <Button
                          variant="contained"
                          color="error"
                          size="large"
                          fullWidth
                          onClick={handleOpen}
                        >
                          Cancelar
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          sx={{
                            backgroundColor: "teal",
                            "&:hover": {
                              backgroundColor: "darkcyan",
                            },
                          }}
                        >
                          Guardar
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Container>
          {/*////////////////  MODAL CANCELAR  ///////////////////*/}
          <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            slotProps={{ backdrop: { style: { opacity: 0.2 } } }}
          >
            <Box sx={style}>
              <Typography variant="h6" className="text-center" sx={{ mb: 1.5 }}>
                ¿Está seguro de cancelar esta operación?
              </Typography>
              <Divider sx={{ mt: 2 }} />
              <Grid
                container
                sx={{ mt: 1 }}
                spacing={1}
                justifyContent="flex-end"
              >
                <Grid item>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleClose}
                  >
                    No
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    sx={{ bgcolor: "teal", color: "white" }}
                    onClick={(event) => handleLinkCancelar(event, "cancelar")}
                  >
                    Si
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Modal>
        </>
      ) : (
        <Restricted />
      )}
    </Box>
  );
}

export default EditarCapForm;
